<script setup lang="ts">
  import type { HTMLAttributes, Component } from "vue";
  import { useVModel } from "@vueuse/core";
  import { X } from "lucide-vue-next";
  import { useSlots, computed, ref } from "vue";
  import { cn } from "@/lib/utils";

  defineOptions({ inheritAttrs: false });

  const props = defineProps<{
    defaultValue?: string | number;
    modelValue?: string | number;
    class?: HTMLAttributes["class"];
    inputClass?: HTMLAttributes["class"];
    icon?: Component;
    clearable?: boolean;
  }>();

  const emits = defineEmits<{
    "update:modelValue": [value: string | number];
  }>();

  const modelValue = useVModel(props, "modelValue", emits, {
    passive: true,
    defaultValue: props.defaultValue,
  });

  const slots = useSlots();
  const hasWrapper = computed(() => !!slots.prepend || !!slots.append || !!props.icon || props.clearable);
  const showClear = computed(() => props.clearable && modelValue.value);
  const clear = () => (modelValue.value = "");

  const input = ref<HTMLInputElement | null>(null);
  defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div
    v-if="hasWrapper"
    data-slot="input-wrapper"
    :class="
      cn(
        'border-input flex h-8 w-full items-center gap-2 rounded-md border bg-background px-3 py-1 shadow-xs transition-[color,box-shadow]',
        'has-[:focus-visible]:outline-none has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-ring',
        'has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        props.class,
      )
    ">
    <component
      :is="props.icon"
      v-if="props.icon"
      class="h-4 w-4 text-muted-foreground" />
    <slot name="prepend" />
    <input
      ref="input"
      v-model="modelValue"
      data-slot="input"
      v-bind="$attrs"
      :class="
        cn(
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex min-w-0 flex-1 bg-transparent text-13 outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-13 file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed',
          props.inputClass,
        )
      " />
    <slot name="append" />
    <X
      v-if="showClear"
      class="h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
      @click="clear" />
  </div>

  <input
    v-else
    ref="input"
    v-model="modelValue"
    data-slot="input"
    v-bind="$attrs"
    :class="
      cn(
        'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-8 w-full min-w-0 rounded-md border bg-background px-3 py-1 text-13 shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-13 file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        props.class,
      )
    " />
</template>
